import { match } from 'ts-pattern';
import { Option } from '@sapphire/result';

import {
  ADVANCED_FILTER_TYPE,
  AdvancedFeasibilityFilters,
  ComputeFeasibilityParams,
} from '@ps/survey-utils';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from '../../audience/survey-creation-audience.actions';
import { SurveyCreationInsightsActions } from '../survey-creation-insights.actions';
import {
  RecalculationData,
  RecalculationTrigger,
} from './survey-creation-recalculation.types';

// True whenever Score/Feasibility should show as pending: either it is
// actually about to recalculate, or it can't right now because manual
// processing is blocking it — and will stay pending until Save or
// toggling manual processing back off clears it.
export function shouldMarkAsPending(action: RecalculationTrigger): boolean {
  return match(action)
    .with(
      { type: SurveyCreationActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .otherwise(() => true);
}

// Defines which actions should trigger a recalculation of Score and Feasibility.
export function qualifiesForRecalculation(
  action: RecalculationTrigger,
  enableManualProcessing: boolean,
): boolean {
  return match(action)
    .with(
      { type: SurveyCreationActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .with(
      { type: SurveyCreationApiActions.loadSurveyForEditSuccess.type },
      { type: SurveyCreationInsightsActions.refreshAfterSave.type },
      () => true,
    )
    .otherwise(() => !enableManualProcessing);
}

/**
 * Params for the single feasibility request a recalculation issues.
 *
 * The grouping only rides along on an Exclude survey whose buyer asked for the
 * exclusions to be applied and which has at least one group; `isExclusion`
 * always follows the committed filter type, grouped or not. Include never
 * sends a grouping — legacy parity, see the plan's §9.2.
 */
export function computeFeasibilityParams(
  data: RecalculationData,
  advancedFilters: AdvancedFeasibilityFilters,
  supplierIds: number[],
  offerPrice: Option<number>,
): ComputeFeasibilityParams {
  const isExclusion = advancedFilters.type === ADVANCED_FILTER_TYPE.EXCLUDE;

  // PD-17726: the filter *type* describes the request, and only the groups it
  // applies to vary — so an ungrouped request zeroes the grouping, not the type.
  // `is_exclusion: true` alongside an empty `survey_grouping` therefore means
  // "excluding nothing", not "exclude everything".
  const base: ComputeFeasibilityParams = {
    surveyId: data.surveyId,
    user: data.user,
    metadata: data.metadata,
    qualifications: data.qualifications,
    quotas: data.quotas,
    offerPrice,
    supplierIds,
    surveyGroupIds: [],
    isExclusion,
    groupingStatuses: [],
  };

  if (!isExclusion || !advancedFilters.excludeFromFeasibleAudience) {
    return base;
  }

  const allGroupIds = [
    ...new Set([
      ...advancedFilters.matchedGroupIds,
      ...advancedFilters.manualGroupIds,
    ]),
  ];

  if (!allGroupIds.length) {
    return base;
  }

  return {
    ...base,
    surveyGroupIds: allGroupIds,
    groupingStatuses: [...advancedFilters.respondentStatuses],
  };
}
